/**
 * Where to draw a line for "the music is here" on a drawn page.
 *
 * The line goes across the whole bar -- every staff of the system -- because the moment is
 * the same for every part. Across the bar it is placed by time, not by distance: an engraver
 * spaces notes by their length only roughly, and a clef, key or time signature at the start
 * of a system pushes the first note well right of the bar line. So the moments that were
 * drawn are the landmarks: the line stands at a note's left edge at the instant it starts,
 * and between two moments it moves evenly from one to the next. After the last moment it
 * runs on to the bar line at the end.
 *
 * Its height is the system's, not the bar's: a bar's drawn box takes in its lyrics, slurs and
 * ledger lines, so it differs from bar to bar, and a line that grew and shrank as it crossed
 * the system would read as something happening in the music. The system is every drawn bar
 * that shares height with this one -- systems are set one under the other and never overlap.
 */

import type { Box } from "./geometry";
import type { DrawnBar } from "./slots";

/** A place in a bar, as the playback layer's `Moment` gives it; only these three are read. */
export interface PlayheadPosition {
  /** Index of the bar in the score. */
  readonly index: number;
  /** Ticks from the start of the bar. */
  readonly tick: number;
  /** How many ticks the bar holds. */
  readonly length: number;
}

/** One landmark: the moment a tick was drawn at, and where. */
interface Landmark {
  readonly tick: number;
  readonly x: number;
}

/** The leftmost drawn edge of every moment in the bar, in time order. */
function landmarks(bar: DrawnBar): readonly Landmark[] {
  const leftmost = new Map<number, number>();
  for (const voice of bar.voices) {
    for (const slot of voice.slots) {
      const was = leftmost.get(slot.onset);
      if (was === undefined || slot.box.x < was) leftmost.set(slot.onset, slot.box.x);
    }
  }
  return [...leftmost.entries()]
    .map(([tick, x]) => ({ tick, x }))
    .sort((one, other) => one.tick - other.tick);
}

/**
 * The line for a place in a bar, or `null` when the bar is not drawn among `bars` -- it is on
 * another page, or the engine did not draw it.
 */
export function playheadBox(
  bars: readonly DrawnBar[],
  at: PlayheadPosition,
  width = 2,
): Box | null {
  const bar = bars.find((candidate) => candidate.measureIndex === at.index);
  if (bar === undefined) return null;

  const end = { tick: at.length, x: bar.box.x + bar.box.width };
  const marks = landmarks(bar);
  const first = marks[0];
  const points =
    first === undefined || first.tick > 0 ? [{ tick: 0, x: bar.box.x }, ...marks] : marks;
  const stops = end.tick > (points[points.length - 1]?.tick ?? -1) ? [...points, end] : points;

  let x = stops[0]?.x ?? bar.box.x;
  for (let step = 1; step < stops.length; step += 1) {
    const from = stops[step - 1];
    const to = stops[step];
    if (from === undefined || to === undefined || at.tick < from.tick) break;
    const along =
      to.tick > from.tick ? Math.min(1, (at.tick - from.tick) / (to.tick - from.tick)) : 1;
    x = from.x + along * (to.x - from.x);
  }

  const system = systemOf(bars, bar.box);
  return { x: x - width / 2, y: system.top, width, height: system.bottom - system.top };
}

/** The top and bottom of every drawn bar level with `box`, which is the system it is in. */
function systemOf(
  bars: readonly DrawnBar[],
  box: Box,
): { readonly top: number; readonly bottom: number } {
  let top = box.y;
  let bottom = box.y + box.height;
  for (const other of bars) {
    const level = other.box.y < box.y + box.height && other.box.y + other.box.height > box.y;
    if (!level) continue;
    top = Math.min(top, other.box.y);
    bottom = Math.max(bottom, other.box.y + other.box.height);
  }
  return { top, bottom };
}
